import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { Card } from '@shared/ui/Card';
import { Progress } from '@shared/ui/Progress';
import { useAdminSystemQuery } from '@shared/api';
import { MaintenanceCard } from './MaintenanceCard';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(5)};
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const StatLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const StatSubtle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HostInfo = styled(Card)`
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(5)};
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const InfoLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, monospace;
`;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч ${m}м`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м`;
}

const MAX_CPU_SAMPLES = 60;

export function AdminSystemTab() {
  const theme = useTheme();
  const { data } = useAdminSystemQuery(undefined, { pollingInterval: 5_000 });
  const [cpuHistory, setCpuHistory] = useState<{ t: number; v: number }[]>([]);
  const lastSample = useRef<number>(0);

  useEffect(() => {
    if (!data) return;
    const now = Date.now();
    if (now - lastSample.current < 4000) return; // дедуп
    lastSample.current = now;
    setCpuHistory((prev) => {
      const next = [...prev, { t: now, v: data.cpu_percent }];
      return next.slice(-MAX_CPU_SAMPLES);
    });
  }, [data]);

  const ramPercent = data ? (data.ram_used_bytes / data.ram_total_bytes) * 100 : 0;
  const diskPercent = data ? (data.disk_used_bytes / data.disk_total_bytes) * 100 : 0;
  const cpuPercent = data?.cpu_percent ?? 0;

  return (
    <>
      <Grid>
        <StatCard>
          <StatHeader>
            <StatLabel>CPU</StatLabel>
            <StatSubtle>{data?.cpu_cores ?? 0} ядер</StatSubtle>
          </StatHeader>
          <StatValue>{cpuPercent.toFixed(1)}%</StatValue>
          <Progress percent={cpuPercent} />
          <div style={{ height: 80, marginTop: 8 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cpuHistory} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cpu-mini" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={theme.colors.primary} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={theme.colors.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={theme.colors.primary}
                  strokeWidth={1.5}
                  fill="url(#cpu-mini)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <StatSubtle>Реальное время (5s) · {cpuHistory.length}/60 samples</StatSubtle>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>RAM</StatLabel>
            <StatSubtle>{formatBytes(data?.ram_total_bytes ?? 0)} total</StatSubtle>
          </StatHeader>
          <StatValue>{ramPercent.toFixed(1)}%</StatValue>
          <Progress percent={ramPercent} />
          <StatSubtle>
            {formatBytes(data?.ram_used_bytes ?? 0)} использовано /{' '}
            {formatBytes((data?.ram_total_bytes ?? 0) - (data?.ram_used_bytes ?? 0))} свободно
          </StatSubtle>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Диск</StatLabel>
            <StatSubtle>{formatBytes(data?.disk_total_bytes ?? 0)} total</StatSubtle>
          </StatHeader>
          <StatValue>{diskPercent.toFixed(1)}%</StatValue>
          <Progress percent={diskPercent} />
          <StatSubtle>
            {formatBytes(data?.disk_used_bytes ?? 0)} использовано /{' '}
            {formatBytes((data?.disk_total_bytes ?? 0) - (data?.disk_used_bytes ?? 0))} свободно
          </StatSubtle>
        </StatCard>
      </Grid>

      <HostInfo>
        <InfoRow>
          <InfoLabel>Hostname</InfoLabel>
          <InfoValue>{data?.hostname ?? '—'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Uptime</InfoLabel>
          <InfoValue>{data ? formatUptime(data.uptime_seconds) : '—'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>CPU cores</InfoLabel>
          <InfoValue>{data?.cpu_cores ?? '—'}</InfoValue>
        </InfoRow>
      </HostInfo>

      <MaintenanceCard />
    </>
  );
}
